import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Progress,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Spinner,
  Alert
} from "reactstrap"
import { Link } from "react-router-dom"
import Chart from "react-apexcharts"
import PaymentRecordsModal from "./PaymentRecordsModal"
import RFQDetailsModal from "./RFQDetailsModal"
import StatusDetailModal from "./StatusDetailModal"
import ReworkMilestonesModal from "./ReworkMilestonesModal"
import InfoButton from "./InfoButton"
import "./ClickableCards.css"
import {
  formatCurrency,
  formatPercentage,
  getStatusColor,
  getChartColors,
  processChartData,
  truncateText,
  getRFQProjectAnalytics,
  getRFQProjectAnalyticsDetails,
  exportRFQProjectAnalyticsDetails
} from "../../../../helpers/rfq_dashboard_api"

const DashboardOverview = ({ data, filters, onRefresh }) => {
  // Modal state for payment records
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null)

  // Modal state for RFQ details
  const [rfqModalOpen, setRfqModalOpen] = useState(false)
  const [selectedRfqType, setSelectedRfqType] = useState(null)

  // Modal state for status distribution
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [selectedStatusData, setSelectedStatusData] = useState(null)

  // Modal state for rework milestones
  const [reworkModalOpen, setReworkModalOpen] = useState(false)

  // State for project analytics data
  const [projectAnalyticsData, setProjectAnalyticsData] = useState([])
  const [projectAnalyticsLoading, setProjectAnalyticsLoading] = useState(false)

  // Modal state for project details
  const [projectModalOpen, setProjectModalOpen] = useState(false)
  const [projectModalTitle, setProjectModalTitle] = useState('')
  const [projectModalData, setProjectModalData] = useState([])
  const [projectModalLoading, setProjectModalLoading] = useState(false)
  const [projectModalPage, setProjectModalPage] = useState(1)
  const [projectModalSearch, setProjectModalSearch] = useState('')
  const [projectModalPerPage, setProjectModalPerPage] = useState(10)
  const [projectModalProjectId, setProjectModalProjectId] = useState(null)
  const [exportLoading, setExportLoading] = useState(false)

  // Load project analytics data
  useEffect(() => {
    const loadProjectAnalytics = async () => {
      try {
        setProjectAnalyticsLoading(true)
        console.log("Loading project analytics for Overview tab with filters:", filters)

        const response = await getRFQProjectAnalytics(filters)
        console.log("Project analytics response for Overview:", response)

        if (response && response.status && response.data && response.data.project_analytics) {
          setProjectAnalyticsData(response.data.project_analytics)
          console.log("Project analytics loaded successfully for Overview:", response.data.project_analytics)
        } else {
          console.warn("Failed to load project analytics, using empty array")
          setProjectAnalyticsData([])
        }
      } catch (error) {
        console.error("Error loading project analytics for Overview:", error)
        setProjectAnalyticsData([])
      } finally {
        setProjectAnalyticsLoading(false)
      }
    }

    loadProjectAnalytics()
  }, [filters])

  if (!data) {
    return (
      <div className="text-center py-5">
        <i className="bx bx-data display-4 text-muted"></i>
        <h5 className="mt-3">No data available</h5>
        <p className="text-muted">Please adjust your filters and try again.</p>
      </div>
    )
  }

  const { summary, financial_overview, payment_summary, status_distribution, recent_activities } = data

  // Debug payment summary data
  console.log("DashboardOverview - Full data received:", data)
  console.log("DashboardOverview - payment_summary:", payment_summary)
  console.log("DashboardOverview - payment_summary type:", typeof payment_summary)
  console.log("DashboardOverview - payment_summary length:", payment_summary?.length)

  // Debug status distribution data (keeping for backward compatibility)
  console.log("DashboardOverview - status_distribution:", status_distribution)
  console.log("DashboardOverview - status_distribution type:", typeof status_distribution)
  console.log("DashboardOverview - status_distribution length:", status_distribution?.length)




  // Handle summary card click
  const handleSummaryCardClick = (type) => {
    console.log("Summary card clicked - type:", type)

    if (type === 'rework') {
      // For rework, show milestones modal instead of RFQ modal
      setReworkModalOpen(true)
    } else {
      // For other types, show RFQ modal
      setSelectedRfqType(type)
      setRfqModalOpen(true)
    }
  }





  // Handle payment chart click
  const handlePaymentChartClick = (event, chartContext, config) => {
    if (config.dataPointIndex >= 0) {
      const clickedStatus = paymentChartData.labels[config.dataPointIndex]
      console.log("Payment chart clicked - status:", clickedStatus)
      setSelectedPaymentStatus(clickedStatus)
      setPaymentModalOpen(true)
    }
  }

  // Handle project chart click to open modal
  const handleProjectClick = (projectId, projectName) => {
    console.log("Project chart clicked - project:", projectId, projectName)
    setProjectModalTitle(`${projectName} - RFQ Details`)
    setProjectModalProjectId(projectId)
    setProjectModalPage(1)
    setProjectModalSearch('')
    setProjectModalPerPage(10)
    setProjectModalOpen(true)
    loadProjectModalData(projectId, 1, '', 10)
  }

  // Load project modal data
  const loadProjectModalData = async (projectId, page = 1, search = '', perPage = 10) => {
    try {
      setProjectModalLoading(true)

      const params = {
        type: 'rfq_distribution',
        project_id: projectId,
        page: page,
        per_page: perPage,
        search: search
      }

      // Apply global filters but preserve the project_id from chart click
      const filtersToApply = { ...filters }
      if (projectId) {
        // Don't override project_id if it was passed from chart click
        delete filtersToApply.project_id
      }
      Object.assign(params, filtersToApply)

      console.log("Loading project modal data with params:", {
        type: params.type,
        projectId,
        params
      })

      const response = await getRFQProjectAnalyticsDetails(params)
      console.log("Project modal data response:", response)

      if (response && response.status && response.data) {
        setProjectModalData(response.data)
        console.log("Project modal data loaded successfully:", response.data)
        console.log("First record structure:", response.data.records?.[0])
        console.log("Pagination info:", response.data.pagination)
      } else {
        console.error("Failed to load project modal data:", response)
        setProjectModalData({ records: [], pagination: {} })
      }
    } catch (error) {
      console.error("Error loading project modal data:", error)
      setProjectModalData({ records: [], pagination: {} })
    } finally {
      setProjectModalLoading(false)
    }
  }

  // Handle project modal search
  const handleProjectModalSearch = (searchTerm) => {
    setProjectModalSearch(searchTerm)
    setProjectModalPage(1)
    loadProjectModalData(projectModalProjectId, 1, searchTerm, projectModalPerPage)
  }

  // Handle project modal page change
  const handleProjectModalPageChange = (page) => {
    setProjectModalPage(page)
    loadProjectModalData(projectModalProjectId, page, projectModalSearch, projectModalPerPage)
  }

  // Handle project modal export
  const handleProjectModalExport = async () => {
    try {
      setExportLoading(true)

      const params = {
        type: 'rfq_distribution',
        project_id: projectModalProjectId,
        search: projectModalSearch
      }

      // Apply global filters but preserve the project_id from modal
      const filtersToApply = { ...filters }
      if (projectModalProjectId) {
        // Don't override project_id if it was set from modal
        delete filtersToApply.project_id
      }
      Object.assign(params, filtersToApply)

      console.log('handleProjectModalExport params:', {
        projectModalProjectId,
        params
      })

      await exportRFQProjectAnalyticsDetails(params)
    } catch (err) {
      console.error("Export error:", err)
      alert("Export failed. Please try again.")
    } finally {
      setExportLoading(false)
    }
  }

  // Get payment status specific colors with distinct colors for each status
  const getPaymentStatusColors = (labels) => {
    const colorMap = {
      'paid': '#20c997',        // Teal Green for paid (success)
      'pending': '#fd7e14',     // Orange for pending (warning)
      'rejected': '#e74c3c',    // Red for rejected (danger)
      // Handle capitalized versions from backend
      'Paid': '#20c997',        // Teal Green
      'Pending': '#fd7e14',     // Orange
      'Rejected': '#e74c3c'     // Red
    }

    return labels.map(label => colorMap[label] || colorMap[label.toLowerCase()] || '#6c757d')
  }

  // Prepare payment summary chart
  const paymentChartData = processChartData(payment_summary, 'status', 'total_paid')
  const paymentChartOptions = {
    chart: {
      type: 'bar',
      height: 300,
      events: {
        dataPointSelection: handlePaymentChartClick
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded',
        distributed: true
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: paymentChartData.labels,
    },
    yaxis: {
      title: {
        text: 'Amount (₹)'
      },
      labels: {
        formatter: function (val) {
          return formatCurrency(val)
        }
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return formatCurrency(val)
        }
      },
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const status = w.globals.labels[dataPointIndex]
        const value = series[seriesIndex][dataPointIndex]
        return `
          <div class="payment-chart-tooltip">
            <div><strong>${status}</strong></div>
            <div>Amount: ${formatCurrency(value)}</div>
            <div class="text-muted small">Click to view details</div>
          </div>
        `
      }
    },
    colors: getPaymentStatusColors(paymentChartData.labels),
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.1
        }
      }
    },
    plotOptions: {
      bar: {
        columnWidth: '60%',
        borderRadius: 4,
        distributed: true
      }
    }
  }

  // RFQ Distribution by Project chart configuration
  const rfqDistributionOptions = {
    chart: {
      type: 'bar',
      height: 350,
      events: {
        dataPointSelection: function(event, chartContext, config) {
          const projectIndex = config.dataPointIndex
          const project = projectAnalyticsData[projectIndex]
          if (project) {
            console.log("RFQ Distribution chart clicked - project:", project)
            handleProjectClick(project.project_id, project.project_name)
          }
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded'
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: projectAnalyticsData.map(project => truncateText(project.project_name, 15)),
      title: {
        text: 'Projects'
      }
    },
    yaxis: {
      title: {
        text: 'Number of RFQs'
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val + " RFQs"
        }
      }
    },
    colors: getChartColors(),
    legend: {
      position: 'top',
      horizontalAlign: 'left'
    }
  }

  // Check if there's any assigned RFQ data across all projects
  const hasAssignedData = projectAnalyticsData.some(project => parseInt(project.assigned_rfqs) > 0)

  const rfqDistributionSeries = hasAssignedData ? [
    {
      name: 'Total RFQs',
      data: projectAnalyticsData.map(project => parseInt(project.total_rfqs) || 0)
    },
    {
      name: 'Assigned RFQs',
      data: projectAnalyticsData.map(project => parseInt(project.assigned_rfqs) || 0)
    },
    {
      name: 'Closed RFQs',
      data: projectAnalyticsData.map(project => parseInt(project.closed_rfqs) || 0)
    }
  ] : [
    {
      name: 'Total RFQs',
      data: projectAnalyticsData.map(project => parseInt(project.total_rfqs) || 0)
    },
    {
      name: 'Closed RFQs',
      data: projectAnalyticsData.map(project => parseInt(project.closed_rfqs) || 0)
    }
  ]

  return (
    <div className="dashboard-overview fade-in">
      {/* Summary Cards */}
      <div className="summary-grid">
        <Card
          className="metric-card clickable-card"
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => handleSummaryCardClick('total')}
        >
          <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
            <InfoButton
              type="total_rfqs"
              title="Total RFQs - Business Logic & Calculation"
              size="sm"
              
            />
          </div>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{summary.total_rfqs || 0}</div>
              <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                Total RFQs
                <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                  <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                </small>
              </div>
            </div>
            <div className="metric-icon">
              <i className="bx bx-file-blank"></i>
            </div>
          </div>
        </Card>

        <Card
          className="metric-card success clickable-card"
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => handleSummaryCardClick('active')}
        >
          <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
            <InfoButton
              type="active_rfqs"
              title="Active RFQs - Business Logic & Calculation"
              size="sm"
              
            />
          </div>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{summary.active_rfqs || 0}</div>
              <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                Active RFQs
                <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                  <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                </small>
              </div>
            </div>
            <div className="metric-icon">
              <i className="bx bx-check-circle"></i>
            </div>
          </div>
        </Card>

        <Card
          className="metric-card info clickable-card"
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => handleSummaryCardClick('assigned')}
        >
          <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
            <InfoButton
              type="assigned_rfqs"
              title="Assigned RFQs - Business Logic & Calculation"
              size="sm"
              
            />
          </div>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{summary.assigned_rfqs || 0}</div>
              <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                Assigned RFQs
                <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d', fontWeight: 'bold'}}>
                  <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                </small>
              </div>
            </div>
            <div className="metric-icon">
              <i className="bx bx-user-check"></i>
            </div>
          </div>
        </Card>

        <Card
          className="metric-card danger clickable-card"
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => handleSummaryCardClick('closed')}
        >
          <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
            <InfoButton
              type="closed_rfqs"
              title="Closed RFQs - Business Logic & Calculation"
              size="sm"
              
            />
          </div>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{summary.closed_rfqs || 0}</div>
              <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                Closed RFQs
                <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                  <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                </small>
              </div>
            </div>
            <div className="metric-icon">
              <i className="bx bx-x-circle"></i>
            </div>
          </div>
        </Card>

        <Card
          className="metric-card danger clickable-card"
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => handleSummaryCardClick('rework')}
        >
          <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
            <InfoButton
              type="rework_rfqs"
              title="Rework RFQs - Business Logic & Calculation"
              size="sm"
              
            />
          </div>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{summary.rework_rfqs || 0}</div>
              <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                Rework RFQs
                <div className="text-info small mt-1" style={{fontSize: '0.75rem'}}>
                  <i className="bx bx-error-circle me-1"></i>
                  RFQs with milestones in rework
                </div>
                <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                  <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                </small>
              </div>
            </div>
            <div className="metric-icon">
              <i className="bx bx-refresh"></i>
            </div>
          </div>
        </Card>

        <Card
          className="metric-card info clickable-card"
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => handleSummaryCardClick('vendor_invited')}
        >
          <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
            <InfoButton
              type="vendor_invited_rfqs"
              title="Vendor Invited RFQs - Business Logic & Calculation"
              size="sm"
              
            />
          </div>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{summary.vendor_invited_rfqs || 0}</div>
              <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                Vendor Invited
                <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                  <i className="bx bx-mouse me-1" style={{color: '#6c757d'}}></i>Click to view details
                </small>
              </div>
            </div>
            <div className="metric-icon">
              <i className="bx bx-user-plus"></i>
            </div>
          </div>
        </Card>

        <Card
          className="metric-card success clickable-card"
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => handleSummaryCardClick('work_in_progress')}
        >
          <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
            <InfoButton
              type="work_in_progress_rfqs"
              title="Work in Progress RFQs - Business Logic & Calculation"
              size="sm"
              
            />
          </div>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <div className="metric-value" style={{color: '#495057', fontSize: '2rem', fontWeight: 'bold'}}>{summary.work_in_progress_rfqs || 0}</div>
              <div className="metric-label" style={{color: '#6c757d', fontSize: '0.875rem'}}>
                Work in Progress
                <small className="d-block text-muted" style={{fontSize: '0.7rem', color: '#6c757d'}}>
                  <i className="bx bx-mouse me-1" style={{color: '#8d99a3ff'}}></i>Click to view details
                </small>
              </div>
            </div>
            <div className="metric-icon">
              <i className="bx bx-cog"></i>
            </div>
          </div>
        </Card>
      </div>

      {/* Payment Summary and Recent Activities */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-start">
                <div>
                  <i className="bx bx-credit-card me-2"></i>
                  Payment Summary
                  <small className="text-muted d-block" style={{fontSize: '0.75rem', fontWeight: 'normal'}}>
                    <i className="bx bx-mouse me-1"></i>
                    Click on bars to view details
                  </small>
                </div>
                <InfoButton
                  type="payment_summary_chart"
                  title="Payment Summary Chart - Business Logic & Calculation"
                  size="sm"
                />
              </CardTitle>
              {payment_summary && payment_summary.length > 0 ? (
                <div className="clickable-chart" style={{cursor: 'pointer'}}>
                  <Chart
                    options={paymentChartOptions}
                    series={[{
                      name: 'Amount',
                      data: paymentChartData.series
                    }]}
                    type="bar"
                    height={319}
                  />
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bx bx-credit-card display-4 text-muted"></i>
                  <p className="text-muted mt-2">No payment data available</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-time me-2"></i>
                  Recent Activities
                </span>
                <InfoButton
                  type="recent_activities"
                  title="Recent Activities - Business Logic & Data Source"
                  size="sm"
                />
              </CardTitle>
              {recent_activities && recent_activities.length > 0 ? (
                <div className="activity-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {recent_activities.map((activity, index) => (
                    <div key={`activity-${activity.id || index}`} className="activity-item">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="activity-title">
                            <Link to={`/project-management/view-rfq-quotes/${activity.id}`}>
                              {activity.rfq_code}
                            </Link>
                          </div>
                          <div className="activity-description">
                            {truncateText(activity.rfq_title, 40)}
                          </div>
                          <div className="activity-time text-muted">
                            {activity.project_name} • {activity.vendor_name}
                          </div>
                        </div>
                        <div className="text-end">
                          <Badge color={getStatusColor(activity.current_status)}>
                            {activity.current_status}
                          </Badge>
                          <div className="text-muted small mt-1">
                            {formatCurrency(activity.total_final_amount)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bx bx-time display-4 text-muted"></i>
                  <p className="text-muted mt-2">No recent activities</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* RFQ Distribution by Project - Full Width */}
      <Row>
        <Col lg={12}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-start">
                <div>
                  <i className="bx bx-bar-chart-alt me-2"></i>
                  RFQ Status Distribution by Project
                  <small className="d-block text-muted mt-1" style={{fontSize: '0.75rem'}}>
                    <i className="bx bx-mouse me-1"></i>
                    Click on bars to view project details
                  </small>
                </div>
                <InfoButton
                  type="project_analytics"
                  title="RFQ Distribution Chart - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              {projectAnalyticsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading project data...</p>
                </div>
              ) : projectAnalyticsData && projectAnalyticsData.length > 0 ? (
                <div className="clickable-chart" style={{cursor: 'pointer'}}>
                  <Chart
                    options={rfqDistributionOptions}
                    series={rfqDistributionSeries}
                    type="bar"
                    height={350}
                  />
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bx bx-bar-chart-alt display-4 text-muted"></i>
                  <p className="text-muted mt-2">
                    {projectAnalyticsData && projectAnalyticsData.length === 0
                      ? "No project data available"
                      : "Loading project data..."}
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Payment Records Modal */}
      <PaymentRecordsModal
        isOpen={paymentModalOpen}
        toggle={() => setPaymentModalOpen(false)}
        status={selectedPaymentStatus}
        filters={filters}
        title="Payment Records"
      />

      {/* RFQ Details Modal */}
      <RFQDetailsModal
        isOpen={rfqModalOpen}
        toggle={() => setRfqModalOpen(false)}
        type={selectedRfqType}
        filters={filters}
        title="RFQ Details"
      />

      {/* Status Distribution Modal */}
      <StatusDetailModal
        isOpen={statusModalOpen}
        toggle={() => setStatusModalOpen(false)}
        statusData={selectedStatusData}
        filters={filters}
      />

      {/* Rework Milestones Modal */}
      <ReworkMilestonesModal
        isOpen={reworkModalOpen}
        toggle={() => setReworkModalOpen(false)}
        filters={filters}
        title="Rework Milestones Details"
      />

      {/* Project RFQ Details Modal */}
      <Modal isOpen={projectModalOpen} toggle={() => setProjectModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setProjectModalOpen(false)}>
          <i className="bx bx-detail me-2"></i>
          {projectModalTitle}
        </ModalHeader>
        <ModalBody>
          {/* Search and Controls - Always visible */}
          <Row className="mb-3">
            <Col md={4}>
              <InputGroup>
                <InputGroupText>
                  <i className="bx bx-search"></i>
                </InputGroupText>
                <Input
                  type="text"
                  placeholder="Search RFQs..."
                  value={projectModalSearch}
                  onChange={(e) => handleProjectModalSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <div className="d-flex align-items-center">
                <span className="me-2 text-nowrap">Show:</span>
                <Input
                  type="select"
                  value={projectModalPerPage}
                  onChange={(e) => {
                    const newPerPage = parseInt(e.target.value)
                    setProjectModalPerPage(newPerPage)
                    setProjectModalPage(1) // Reset to first page when changing page size
                    loadProjectModalData(projectModalProjectId, 1, projectModalSearch, newPerPage)
                  }}
                  style={{ fontSize: '0.875rem' }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <Button
                color="success"
                size="sm"
                onClick={handleProjectModalExport}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="bx bx-download me-1"></i>
                    Export CSV
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* Data Section with Loading State */}
          {projectModalLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Loading project details...</p>
            </div>
          ) : (
            <>
              {/* Data Table */}
              {projectModalData && projectModalData.records && projectModalData.records.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <Table className="table-nowrap table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>RFQ#</th>
                          <th>RFQ Title</th>
                          <th>Project</th>
                          <th>Vendor</th>
                          <th>Status</th>
                          <th>Budget</th>
                          <th>Final Amount</th>
                          <th>Estimated Date</th>
                          <th>Completion Date</th>
                          <th>Created Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectModalData.records.map((record, index) => (
                          <tr key={index}>
                            <td>
                              <strong>{record.rfq_code}</strong>
                            </td>
                            <td>{truncateText(record.rfq_title, 30)}</td>
                            <td>
                              <div>
                                <strong>{record.project_name}</strong>
                                <div>
                                  <small className="text-muted">ID: {record.project_id}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <small>{record.vendor_name || 'Unassigned'}</small>
                            </td>
                            <td>
                              <Badge color="primary" className="badge-soft">
                                {record.status}
                              </Badge>
                            </td>
                            <td>
                              <strong>{formatCurrency(record.budget)}</strong>
                            </td>
                            <td>
                              <strong className="text-success">
                                {formatCurrency(record.total_final_amount)}
                              </strong>
                            </td>
                            <td>
                              {record.estimated_completion_date ? (
                                <small className="text-info">
                                  <i className="bx bx-calendar me-1"></i>
                                  {record.estimated_completion_date}
                                </small>
                              ) : (
                                <small className="text-muted">Not Set</small>
                              )}
                            </td>
                            <td>
                              {record.completion_date ? (
                                <small className="text-success">
                                  <i className="bx bx-check-circle me-1"></i>
                                  {record.completion_date}
                                </small>
                              ) : (
                                <small className="text-muted">-</small>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(record.created_at).toLocaleDateString()}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination and Entry Count */}
                  {projectModalData.pagination && (
                    <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                      <div className="text-muted">
                        Showing {((projectModalData.pagination.current_page - 1) * projectModalData.pagination.per_page) + 1} to{' '}
                        {Math.min(projectModalData.pagination.current_page * projectModalData.pagination.per_page, projectModalData.pagination.total_records)} of{' '}
                        {projectModalData.pagination.total_records} entries
                      </div>
                      {projectModalData.pagination.total_pages > 1 && (
                        <nav>
                          <ul className="pagination mb-0">
                            <li className={`page-item ${projectModalPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handleProjectModalPageChange(projectModalPage - 1)}
                                disabled={projectModalPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            {[...Array(Math.min(5, projectModalData.pagination.total_pages))].map((_, i) => {
                              const page = i + 1
                              return (
                                <li key={page} className={`page-item ${projectModalPage === page ? 'active' : ''}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => handleProjectModalPageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            })}
                            <li className={`page-item ${projectModalPage === projectModalData.pagination.total_pages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handleProjectModalPageChange(projectModalPage + 1)}
                                disabled={projectModalPage === projectModalData.pagination.total_pages}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <i className="bx bx-search-alt-2 display-4 text-muted"></i>
                  <p className="text-muted mt-2">
                    {projectModalSearch ?
                      `No RFQs found matching "${projectModalSearch}"` :
                      'No RFQs available for this project'
                    }
                  </p>
                </div>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default DashboardOverview
